/**
 * Shared WebAudio analyser → music bands.
 * Port of FreeMusiers player band math, simplified for games.
 */

export type MusicBands = {
  kick: number;
  bass: number;
  mids: number;
  highs: number;
  energy: number;
  pulse: number;
  /** 0–1 onset-ish gate for beat events */
  beat: number;
};

const EMPTY: MusicBands = {
  kick: 0,
  bass: 0,
  mids: 0,
  highs: 0,
  energy: 0,
  pulse: 0,
  beat: 0,
};

export class MusicEngine {
  readonly audio: HTMLAudioElement;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private freq = new Uint8Array(0);
  private time = new Uint8Array(0);
  /** Normalized −1…1 time-domain samples (copy updated each tick) */
  private waveNorm = new Float32Array(0);
  private smooth = { kick: 0, bass: 0, mids: 0, highs: 0, energy: 0 };
  private peak = { kick: 0.14, bass: 0.14, mids: 0.14, highs: 0.14, energy: 0.14 };
  private lastEnergy = 0;
  private bands: MusicBands = { ...EMPTY };
  private unlocked = false;

  constructor() {
    this.audio = new Audio();
    this.audio.crossOrigin = 'anonymous';
    this.audio.preload = 'auto';
    this.audio.setAttribute('playsinline', 'true');
    this.audio.setAttribute('webkit-playsinline', 'true');
  }

  get current(): MusicBands {
    return this.bands;
  }

  /** Latest time-domain samples in −1…1. Empty when silent/unready. */
  get waveform(): Float32Array {
    return this.waveNorm;
  }

  get isPlaying(): boolean {
    return !this.audio.paused && !!this.audio.src;
  }

  async unlock(): Promise<void> {
    if (this.unlocked && this.ctx?.state === 'running') return;
    if (!this.ctx) {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.72;
      this.freq = new Uint8Array(this.analyser.frequencyBinCount);
      this.time = new Uint8Array(this.analyser.fftSize);
      this.waveNorm = new Float32Array(this.analyser.fftSize);
      this.source = this.ctx.createMediaElementSource(this.audio);
      this.source.connect(this.analyser);
      this.analyser.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    this.unlocked = true;
  }

  async load(url: string): Promise<void> {
    await this.unlock();
    this.audio.pause();
    this.audio.src = url;
    this.audio.load();
    await new Promise<void>((resolve, reject) => {
      const ok = () => {
        cleanup();
        resolve();
      };
      const bad = () => {
        cleanup();
        reject(new Error('Audio failed to load'));
      };
      const cleanup = () => {
        this.audio.removeEventListener('canplay', ok);
        this.audio.removeEventListener('error', bad);
      };
      this.audio.addEventListener('canplay', ok, { once: true });
      this.audio.addEventListener('error', bad, { once: true });
    });
  }

  async play(): Promise<void> {
    await this.unlock();
    await this.audio.play();
  }

  pause(): void {
    this.audio.pause();
  }

  toggle(): void {
    if (this.audio.paused) void this.play();
    else this.pause();
  }

  setVolume(v: number): void {
    this.audio.volume = Math.max(0, Math.min(1, v));
  }

  /** Call once per frame. */
  tick(): MusicBands {
    if (!this.analyser || this.audio.paused || !this.audio.src) {
      this.bands = {
        kick: this.smooth.kick * 0.9,
        bass: this.smooth.bass * 0.9,
        mids: this.smooth.mids * 0.9,
        highs: this.smooth.highs * 0.9,
        energy: this.smooth.energy * 0.9,
        pulse: 0,
        beat: 0,
      };
      this.waveNorm.fill(0);
      return this.bands;
    }

    this.analyser.getByteFrequencyData(this.freq);
    this.analyser.getByteTimeDomainData(this.time);
    for (let i = 0; i < this.time.length; i++) {
      this.waveNorm[i] = (this.time[i] - 128) / 128;
    }

    const n = this.freq.length;
    const avg = (from: number, to: number) => {
      let s = 0;
      let c = 0;
      const a = Math.max(0, Math.floor(from));
      const b = Math.min(n, Math.ceil(to));
      for (let i = a; i < b; i++) {
        s += this.freq[i];
        c++;
      }
      return c ? s / c / 255 : 0;
    };

    const kickRaw = avg(0, n * 0.04);
    const bassRaw = avg(n * 0.04, n * 0.12);
    const midsRaw = avg(n * 0.12, n * 0.4);
    const highsRaw = avg(n * 0.4, n * 0.9);
    const energyRaw = kickRaw * 0.34 + bassRaw * 0.3 + midsRaw * 0.22 + highsRaw * 0.14;

    const curve = (x: number) => Math.pow(Math.min(1, Math.max(0, x)), 0.85);

    const smoothBand = (
      key: keyof typeof this.smooth,
      target: number,
      up = 0.28,
      down = 0.1,
    ) => {
      const prev = this.smooth[key];
      const next = target > prev ? prev + (target - prev) * up : prev + (target - prev) * down;
      this.smooth[key] = next;
      this.peak[key] = Math.max(this.peak[key] * 0.995, next, 0.12);
      return Math.min(1, next / this.peak[key]);
    };

    const kick = smoothBand('kick', curve(kickRaw), 0.35, 0.12);
    const bass = smoothBand('bass', curve(bassRaw), 0.3, 0.1);
    const mids = smoothBand('mids', curve(midsRaw), 0.25, 0.1);
    const highs = smoothBand('highs', curve(highsRaw), 0.22, 0.1);
    const energy = smoothBand('energy', curve(energyRaw), 0.22, 0.09);

    const pulse = Math.max(0, energy - this.lastEnergy) * 4;
    this.lastEnergy = energy;
    const beat = pulse > 0.12 || (kick > 0.72 && pulse > 0.04) ? Math.min(1, pulse + kick * 0.3) : 0;

    this.bands = { kick, bass, mids, highs, energy, pulse: Math.min(1, pulse), beat };
    return this.bands;
  }

  dispose(): void {
    this.audio.pause();
    this.audio.removeAttribute('src');
    void this.ctx?.close();
    this.ctx = null;
    this.analyser = null;
    this.source = null;
  }
}
